import { useState, ChangeEvent, useEffect, CSSProperties } from 'react';
import { MappedColumns } from "modules/Types";
import { getCsvHeaders_api } from "api/brandApi";
import { MATCH_FILE_TAB_DEFAULT_VALUE as DEFAULT_VALUE } from "pages/FansPage/constants";
import { STEPS } from "./UploadEmailsByCsv";
import CreatableSelectColumn from "./CreatableSelectColumn";
import useUploadEmailsByCsv from "Hooks/useUploadEmailsByCsv";
import useBrand from "Hooks/useBrand";
import { BrandExistsState } from "Hooks/useBrand";
import { makeKey } from "modules/Utils";
import CardSymphony from 'elements/CardSymphony';
import Sty from 'modules/Sty';
import Loader from 'elements/Loader';

const FIRST_COLUMN_WIDTH = 200;

const DEFAULT_COLUMNS_LOCAL = [ // NOTE: this is different from the DEFAULT_COLUMN_MAPPINGS in useUploadEmailsByCsv
    { id: "Name", label: "Name" },
    { id: "LastName", label: "Last Name" },
    { id: "Email", label: "Email" },
    { id: "CellPhone", label: "Cell Phone" },
    { id: "Source", label: "Collected from" },
    { id: "City", label: "City" },
    { id: "State", label: "State" },
    { id: "Country", label: "Country" },
    { id: "ignore-column", label: "Ignore this column" },
];

interface ColumnOption {
    value: string;
    label: string;
}

interface MatchFileTabProps {
    selectedStep: STEPS;
}

const MatchFileTab = ({
    selectedStep,
}: MatchFileTabProps) => {
    const { currentBrand } = useBrand() as BrandExistsState;

    const { MATCH_FILE } = STEPS;

    const {
        mappedColumns,
        setMappedColumns,
    } = useUploadEmailsByCsv();

    const [fanInfoHeaders, setFanInfoHeaders] = useState<{ id: string; label: string }[]>([]);
    const [isLoadingCsvHeaders, setIsLoadingCsvHeaders] = useState<boolean>(false);

    const [availableOptions, setAvailableOptions] = useState<{ id: string; label: string }[]>(DEFAULT_COLUMNS_LOCAL);

    const getCsvHeaders = async () => {
        try {
            setIsLoadingCsvHeaders(true);
            const { data, error } = await getCsvHeaders_api(currentBrand.id);

            if (!error) {
                const headers = (data as string[]).map((header) => ({
                    id: header.trim(),
                    label: header.trim(),
                }));

                setFanInfoHeaders(headers);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingCsvHeaders(false);
        }
    }

    useEffect(() => {
        if (selectedStep === MATCH_FILE) {
            getCsvHeaders();
        }
    }, [selectedStep]);

    const findMatchForHeader = (headerLabel: string): string => {
        const matchedItem = DEFAULT_COLUMNS_LOCAL.find(column => column.label === headerLabel);
        return matchedItem ? matchedItem.id : DEFAULT_VALUE;
    };

    useEffect(() => {
        if (Object.keys(mappedColumns).length === 0 && fanInfoHeaders.length > 0) {
            const initialMapping: MappedColumns = {};
            const usedOptions: string[] = [];

            fanInfoHeaders.forEach(({ id: headerId, label: headerLabel }) => {
                const matchedColumn = findMatchForHeader(headerLabel);
                if (matchedColumn !== DEFAULT_VALUE) {
                    initialMapping[matchedColumn] = headerId;
                    usedOptions.push(matchedColumn);
                }
            });

            // Filter out used options from available options
            const filteredOptions = availableOptions.filter(
                option => !usedOptions.includes(option.id)
            );

            setMappedColumns(initialMapping);
            setAvailableOptions(filteredOptions);
        }
    }, [fanInfoHeaders]);

    const handleChangeInput = (
        e: ChangeEvent<{ value: unknown; name?: string }>
    ) => {
        const name = e.target.name as string;
        const value = e.target.value as string;

        // If this is clearing a selection (setting to DEFAULT_VALUE)
        if (name === DEFAULT_VALUE) {
            // Find which column was previously mapped to this header value
            const previousMapping = Object.entries(mappedColumns).find(([_, v]) => v === value)?.[0];
            if (previousMapping) {
                // Add the previously mapped column back to available options
                const columnToAdd = DEFAULT_COLUMNS_LOCAL.find(col => col.id === previousMapping);
                if (columnToAdd && !availableOptions.some(opt => opt.id === columnToAdd.id)) {
                    setAvailableOptions(prev => [...prev, columnToAdd]);
                }
                // Remove the mapping
                const newMappedColumns = { ...mappedColumns };
                delete newMappedColumns[previousMapping];
                setMappedColumns(newMappedColumns);
            }
            return;
        }

        // Handle new selection
        const newMappedColumns = { ...mappedColumns };

        // If this header was previously mapped to something else
        Object.entries(newMappedColumns).forEach(([key, val]) => {
            if (val === value) {
                delete newMappedColumns[key];
                // Add the previously used column back to available options
                const columnToAdd = DEFAULT_COLUMNS_LOCAL.find(col => col.id === key);
                if (columnToAdd && !availableOptions.some(opt => opt.id === columnToAdd.id)) {
                    setAvailableOptions(prev => [...prev, columnToAdd]);
                }
            }
        });

        // Remove newly selected column from available options
        if (name !== 'ignore-column') {
            setAvailableOptions(prev => prev.filter(opt => opt.id !== name));
            newMappedColumns[name] = value;
        }

        // Update the mapping
        setMappedColumns(newMappedColumns);
    };

    const renderTableHeader = () => {
        return (
            <div
                className="matchFileTabHeaderRow"
                style={sty.matchFileTabHeaderRow}
            >
                <div
                    className="matchFileTabYourCsvColumnsLabel"
                    style={sty.matchFileTabYourCsvColumnsLabel}
                >
                    Your CSV File Columns
                </div>
                <div
                    className="matchFileTabSymphonyDatabaseLabel"
                    style={Sty.text18bold}
                >
                    Display As
                </div>
            </div>
        )
    }

    const renderFanInfoHeaders = () => {
        return fanInfoHeaders.map((fanInfoHeader, index) => {
            const { id: headerId, label: headerLabel } = fanInfoHeader;

            const currentMatch = findMatchForHeader(headerLabel);
            const initialValue = DEFAULT_COLUMNS_LOCAL.find(col => col.id === currentMatch)
                ? { value: currentMatch, label: DEFAULT_COLUMNS_LOCAL.find(col => col.id === currentMatch)?.label || '' }
                : null;

            return (
                <div
                    key={makeKey(headerId, index)}
                    className="matchFileTabMatchRow"
                    style={sty.matchFileTabMatchRow}
                >
                    <div className="matchFileTabMatchLabelContainer" style={sty.matchFileTabMatchLabelContainer}>
                        {headerLabel}
                    </div>
                    <CreatableSelectColumn
                        currentMatch={currentMatch}
                        defaultValue={DEFAULT_VALUE}
                        availableOptions={availableOptions}
                        initialValue={initialValue as ColumnOption | null}
                        handleChangeInput={handleChangeInput}
                        headerLabel={headerLabel}
                    />
                </div>
            );
        })
    }

    // NOTE: render()
    return (
        <CardSymphony
            className="matchFileTabComponent"
            style={sty.matchFileTabComponent}
        >
            <CardSymphony
                className="matchFileTabHeader"
                title="Match your file"
                subtitle="Match your CSV file columns with Symphony's database fields to import your contacts."
                isNoPadding
                size="small"
            />
            {isLoadingCsvHeaders ?
                <Loader
                    className="matchFileTabLoader"
                    size={44}
                    style={{
                        paddingTop: 48,
                        paddingBottom: 48
                    }}
                    title="Loading CSV headers..."
                />
                :
                <div
                    className="matchFileTableContainer"
                    style={sty.matchFileTableContainer}
                >
                    {renderTableHeader()}
                    {renderFanInfoHeaders()}
                </div>
            }
        </CardSymphony >
    );
};

const sty: Record<string, CSSProperties> = {
    matchFileTabComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    databaseLabel: {
        marginLeft: 132,
    },
    matchFileTabHeaderRow: {
        marginTop: 16,
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
    },
    matchFileTabMatchRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
    },
    matchFileTabYourCsvColumnsLabel: {
        ...Sty.text18bold,
        width: FIRST_COLUMN_WIDTH
    },
    matchFileTabMatchLabelContainer: {
        textAlign: 'right',
        width: FIRST_COLUMN_WIDTH,
        paddingRight: 16,
    },
    matchFileTableContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        marginLeft: 'auto',
        marginRight: 'auto',
    }
};

export default MatchFileTab;
